// src/devices/registry.ts
import type { DeviceRecord, Provider } from "./types";

/** Connector contract — replace stubs with real integrations later. */
export interface Connector {
  connect(): Promise<Partial<DeviceRecord>>;
  disconnect(): Promise<void>;
  refresh?(): Promise<Partial<DeviceRecord>>;
}

/** Small utility to simulate network delay deterministically. */
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** OAuth-like providers (Fitbit, Oura, etc.) */
function oauthStub(_p: Provider, battery?: number): Connector {
  return {
    async connect() {
      await delay(600);
      return {
        status: "connected",
        lastSync: "now",
        battery,
        auth: { accessToken: "stub", expiresAt: Date.now() + 3600_000 },
      };
    },
    async disconnect() {
      await delay(200);
    },
    async refresh() {
      await delay(250);
      return { lastSync: "now" };
    },
  };
}

/** BLE / custom stack stubs (Tesla Suit, Lumina, OpenBCI, etc.) */
function bleStub(_p: Provider, battery?: number): Connector {
  return {
    async connect() {
      await delay(800);
      return {
        status: "connected",
        lastSync: "now",
        battery,
        rssi: -58,
      };
    },
    async disconnect() {
      await delay(200);
    },
  };
}

export const connectors: Record<Provider, Connector> = {
  "Fitbit": oauthStub("Fitbit"),
  "Oura": oauthStub("Oura", 74),
  "Apple Watch": oauthStub("Apple Watch"),
  "Google Fit": oauthStub("Google Fit"),
  "Garmin": oauthStub("Garmin"),
  "WHOOP": oauthStub("WHOOP", 61),
  "Polar": oauthStub("Polar"),
  "Suunto": oauthStub("Suunto"),

  "Muse": bleStub("Muse"),
  "OpenBCI": bleStub("OpenBCI"),
  "Emotiv": bleStub("Emotiv"),
  "Meta Quest": bleStub("Meta Quest"),
  "Tesla Suit": bleStub("Tesla Suit", 84),
  "Lumina Vibe": bleStub("Lumina Vibe"),
  "Lumina One": bleStub("Lumina One", 82),
};

// ——— add at the bottom of src/devices/registry.ts ———
import type { DataScope } from "./types";

/**
 * What each provider can share (capability matrix).
 * Use to drive Settings toggles, consent prompts, and UI visibility.
 */
export const providerCapabilities: Record<Provider, DataScope[]> = {
  "Fitbit": ["heart_rate", "steps", "sleep_score", "battery"],
  "Oura": ["heart_rate", "hrv", "sleep_score", "readiness", "battery"],
  "Apple Watch": ["heart_rate", "steps", "sleep_score", "battery"],
  "Google Fit": ["heart_rate", "steps", "sleep_score", "battery"],
  "Garmin": ["heart_rate", "hrv", "steps", "sleep_score", "battery"],
  "WHOOP": ["heart_rate", "hrv", "sleep_score", "readiness", "battery"],
  "Polar": ["heart_rate", "hrv", "steps", "battery"],
  "Suunto": ["heart_rate", "steps", "battery"],

  "Muse": ["breath_coherence", "session_marker", "battery", "rssi"],
  "OpenBCI": ["session_marker", "battery", "rssi"],
  "Emotiv": ["session_marker", "battery", "rssi"],
  "Meta Quest": ["session_marker", "battery", "rssi"],
  "Tesla Suit": ["session_marker", "battery", "rssi"],
  "Lumina Vibe": ["breath_coherence", "session_marker", "battery", "rssi"],
  "Lumina One": ["breath_coherence", "session_marker", "battery", "rssi"],
};

/** Optional: handy helper for UI defaults */
export function getDefaultScopes(provider: Provider): DataScope[] {
  return providerCapabilities[provider] ?? [];
}

